// ============================================================================
// LEDGER MAPPING SERVICE (Fairbooks integration configuration)
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { ledgerMappingEndpoints } from '../endpoints/ledgerMapping.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  FbLedger,
  AccountingLedgerMap,
  UpsertAccountingLedgerMapRequest,
  AccountingLedgerMapSearchRequest,
  LedgerMap,
  UpsertLedgerMapRequest,
  MappingLookups,
} from '../types';

class LedgerMappingService {
  async getLookups(): Promise<ApiResponse<MappingLookups>> {
    const response = await get<ApiResponse<MappingLookups>>(ledgerMappingEndpoints.lookups());
    return response.data;
  }

  /**
   * Fairbooks chart-of-accounts ledgers for the picker. Scoped to the entity the
   * given property posts into (or the tenant's first entity when omitted).
   */
  async getFbLedgers(params?: { propertyId?: ID; search?: string }): Promise<ApiResponse<FbLedger[]>> {
    const endpoint = params && (params.propertyId || params.search)
      ? buildEndpointWithQuery('/ledger-mappings/fb-ledgers', params)
      : ledgerMappingEndpoints.fbLedgers();
    const response = await get<ApiResponse<FbLedger[]>>(endpoint);
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Resolver rows
  // --------------------------------------------------------------------------

  async getAll(params?: AccountingLedgerMapSearchRequest): Promise<PaginatedResponse<AccountingLedgerMap>> {
    const endpoint = params
      ? buildEndpointWithQuery('/ledger-mappings', params)
      : ledgerMappingEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<AccountingLedgerMap>> {
    const response = await get<ApiResponse<AccountingLedgerMap>>(ledgerMappingEndpoints.getById(id));
    return response.data;
  }

  async create(data: UpsertAccountingLedgerMapRequest): Promise<ApiResponse<AccountingLedgerMap>> {
    const response = await post<ApiResponse<AccountingLedgerMap>>(ledgerMappingEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpsertAccountingLedgerMapRequest): Promise<ApiResponse<AccountingLedgerMap>> {
    const response = await put<ApiResponse<AccountingLedgerMap>>(ledgerMappingEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(ledgerMappingEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  // --------------------------------------------------------------------------
  // Transaction mappings (voucher type + legs)
  // --------------------------------------------------------------------------

  async getTransactionMaps(): Promise<ApiResponse<LedgerMap[]>> {
    const response = await get<ApiResponse<LedgerMap[]>>(ledgerMappingEndpoints.transactionMaps());
    return response.data;
  }

  /** Creates or replaces the mapping for (property, transaction type) atomically. */
  async upsertTransactionMap(data: UpsertLedgerMapRequest): Promise<ApiResponse<LedgerMap>> {
    const response = await put<ApiResponse<LedgerMap>>(ledgerMappingEndpoints.transactionMaps(), data);
    return response.data;
  }
}

export const ledgerMappingService = new LedgerMappingService();
export default ledgerMappingService;
